import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button, Input, Label, Select, Card, Spinner, money } from "@/components/ui";
import { toast } from "sonner";
import { Plus, Trash2, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";

const emptyItem = () => ({ description: "", amount: "", type: "one_time" });

export default function NewInvoice() {
  const navigate = useNavigate();
  const [customer, setCustomer] = useState({ name: "", email: "", company: "", phone: "" });
  const [currency, setCurrency] = useState("usd");
  const [items, setItems] = useState([
    { description: "Web Development", amount: "", type: "one_time" },
    { description: "Hosting (annual)", amount: "", type: "one_time" },
    { description: "API & Maintenance", amount: "", type: "monthly" },
  ]);
  const [busy, setBusy] = useState(false);

  function setItem(i, patch) {
    setItems((arr) => arr.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
  }
  function addItem() { setItems((arr) => [...arr, emptyItem()]); }
  function removeItem(i) { setItems((arr) => arr.filter((_, idx) => idx !== i)); }

  const cents = (a) => Math.round((parseFloat(a) || 0) * 100);
  const oneTimeTotal = items.filter((i) => i.type === "one_time").reduce((s, i) => s + cents(i.amount), 0);
  const monthlyTotal = items.filter((i) => i.type === "monthly").reduce((s, i) => s + cents(i.amount), 0);

  async function onSubmit(e) {
    e.preventDefault();
    const clean = items
      .map((i) => ({ description: i.description.trim(), amount: parseFloat(i.amount), type: i.type }))
      .filter((i) => i.description && i.amount > 0);
    if (!customer.name || !customer.email) return toast.error("Customer name and email are required.");
    if (clean.length === 0) return toast.error("Add at least one line item with an amount.");

    setBusy(true);
    try {
      const res = await api.post("/admin/invoices", {
        customer: {
          name: customer.name, email: customer.email,
          company: customer.company || null, phone: customer.phone || null,
        },
        currency,
        line_items: clean,
      });
      toast.success("Invoice created — pay link ready.");
      navigate(`/invoices/${res.data.id}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={onSubmit} className="max-w-3xl mx-auto space-y-6">
      <Link to="/" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" /> Back
      </Link>
      <h1 className="text-2xl font-bold">New invoice</h1>

      <Card className="space-y-4">
        <h2 className="font-semibold">Customer</h2>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label>Name *</Label>
            <Input value={customer.name} onChange={(e) => setCustomer({ ...customer, name: e.target.value })} placeholder="Jane Doe" required />
          </div>
          <div>
            <Label>Email *</Label>
            <Input type="email" value={customer.email} onChange={(e) => setCustomer({ ...customer, email: e.target.value })} placeholder="jane@company.com" required />
          </div>
          <div>
            <Label>Company</Label>
            <Input value={customer.company} onChange={(e) => setCustomer({ ...customer, company: e.target.value })} placeholder="Optional" />
          </div>
          <div>
            <Label>Phone</Label>
            <Input value={customer.phone} onChange={(e) => setCustomer({ ...customer, phone: e.target.value })} placeholder="Optional" />
          </div>
        </div>
      </Card>

      <Card className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold">Line items</h2>
          <Select value={currency} onChange={(e) => setCurrency(e.target.value)} className="w-28">
            <option value="usd">USD</option>
            <option value="cad">CAD</option>
            <option value="eur">EUR</option>
            <option value="gbp">GBP</option>
          </Select>
        </div>

        <div className="space-y-3">
          {items.map((it, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-start">
              <div className="col-span-6">
                <Input placeholder="Description" value={it.description} onChange={(e) => setItem(i, { description: e.target.value })} />
              </div>
              <div className="col-span-3">
                <Input type="number" step="0.01" min="0" placeholder="0.00" value={it.amount} onChange={(e) => setItem(i, { amount: e.target.value })} />
              </div>
              <div className="col-span-2">
                <Select value={it.type} onChange={(e) => setItem(i, { type: e.target.value })}>
                  <option value="one_time">One-time</option>
                  <option value="monthly">Monthly</option>
                </Select>
              </div>
              <div className="col-span-1 flex justify-center pt-1.5">
                <button type="button" onClick={() => removeItem(i)} className="text-muted-foreground hover:text-destructive" title="Remove">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>

        <Button type="button" variant="outline" size="sm" onClick={addItem}>
          <Plus className="h-4 w-4" /> Add line item
        </Button>
      </Card>

      <Card className="space-y-2">
        <div className="flex justify-between text-sm">
          <span className="text-muted-foreground">Due now (one-time + first month)</span>
          <span className="mono font-semibold">{money(oneTimeTotal + monthlyTotal, currency)}</span>
        </div>
        {monthlyTotal > 0 && (
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Then every month</span>
            <span className="mono font-semibold text-primary">{money(monthlyTotal, currency)}/mo</span>
          </div>
        )}
      </Card>

      <div className="flex justify-end gap-3">
        <Link to="/"><Button type="button" variant="ghost">Cancel</Button></Link>
        <Button type="submit" size="lg" disabled={busy}>
          {busy ? <Spinner /> : "Create invoice & get pay link"}
        </Button>
      </div>
    </form>
  );
}
